import {
  AgrupamentoConfig,
  AgrupamentoConfigForm,
  AgrupamentoFiltro,
  AgrupamentoTratamento,
  AgrupamentoVariavel,
  TipoOperacao,
  TipoOperador,
  TipoTransformacaoChave
} from '../../../types/smartinteg';

export const TRANSFORMACOES_CHAVE: { label: string; value: TipoTransformacaoChave | null }[] = [
  { label: 'Valor bruto (sem transformação)', value: null },
  { label: 'Texto', value: 'TEXTO' },
  { label: 'Data (yyyy-MM-dd)', value: 'DATA' },
  { label: 'Ano e mês', value: 'ANO_MES' },
  { label: 'Ano', value: 'ANO' },
  { label: 'Mês (2 dígitos)', value: 'MES' },
  { label: 'Dia', value: 'DIA' }
];

export const OPERACOES: { label: string; value: TipoOperacao }[] = [
  { label: 'Mínimo', value: 'MINIMO' },
  { label: 'Máximo', value: 'MAXIMO' },
  { label: 'Soma', value: 'SOMA' },
  { label: 'Média aritmética', value: 'MEDIA_ARITMETICA' },
  { label: 'Contagem', value: 'CONTAGEM' },
  { label: 'Primeiro (pela ordenação)', value: 'PRIMEIRO' },
  { label: 'Último (pela ordenação)', value: 'ULTIMO' },
  { label: 'Concatenar', value: 'CONCATENAR' },
  { label: 'Fórmula (SpEL)', value: 'FORMULA' }
];

export const OPERADORES: { label: string; value: TipoOperador }[] = [
  { label: 'Igual a', value: 'EQUALS' },
  { label: 'Diferente de', value: 'NOT_EQUALS' },
  { label: 'Menor que', value: 'LESS_THAN' },
  { label: 'Maior que', value: 'GREATHER_THAN' },
  { label: 'Contém', value: 'CONTAINS' },
  { label: 'Não contém', value: 'NOT_CONTAINS' },
  { label: 'Começa com', value: 'STARTS_WITH' },
  { label: 'Termina com', value: 'ENDS_WITH' },
  { label: 'Vazio', value: 'EMPTY' },
  { label: 'Não vazio', value: 'NOT_EMPTY' },
  { label: 'Hora igual a (UTC)', value: 'HORA_IGUAL' },
  { label: 'Data igual a (UTC)', value: 'DATA_IGUAL' }
];

export const DIRECOES = [
  { label: 'Crescente (ASC)', value: 'ASC' },
  { label: 'Decrescente (DESC)', value: 'DESC' }
];

const SEM_VALOR: TipoOperador[] = ['EMPTY', 'NOT_EMPTY'];
const COM_CAMPOS_ADICIONAIS: TipoOperacao[] = ['MINIMO', 'MAXIMO', 'PRIMEIRO', 'ULTIMO'];
const COM_ORDENACAO: TipoOperacao[] = ['PRIMEIRO', 'ULTIMO'];

export const semCampoFonte = (op: TipoOperacao) => op === 'CONTAGEM' || op === 'FORMULA';
export const aceitaCamposAdicionais = (op: TipoOperacao) => COM_CAMPOS_ADICIONAIS.includes(op);
export const aceitaOrdenacao = (op: TipoOperacao) => COM_ORDENACAO.includes(op);
export const operadorSemValor = (o: TipoOperador) => SEM_VALOR.includes(o);

export function novoFiltro(): AgrupamentoFiltro {
  return { campo: '', tipoOperador: 'EQUALS', valor: '' };
}

export function novaVariavel(): AgrupamentoVariavel {
  return { nome: '', campo: '', valorDefault: null, filtros: [] };
}

export function novoTratamento(): AgrupamentoTratamento {
  return { tipoOperacao: 'SOMA', nomeCampo: '', nomeDestino: '', filtros: [], camposAdicionais: [], camposOrdenar: [], separador: '', formula: '', variaveis: [] };
}

export function novoAgrupamentoForm(): AgrupamentoConfigForm {
  return { excluiRegistros: false, chaves: [{ campo: '', transformacao: 'TEXTO' }], camposOrdenar: [], tratamentos: [novoTratamento()] };
}

/** Lê Recurso.configuracoes.agrupamento (formato do backend) para o formulário, completando o que faltar */
export function lerAgrupamento(cfg: Partial<AgrupamentoConfig> | null | undefined): AgrupamentoConfigForm {
  if (!cfg) return novoAgrupamentoForm();
  return {
    excluiRegistros: !!cfg.excluiRegistros,
    chaves: (cfg.chaves ?? []).map(c => ({ campo: c.campo ?? '', transformacao: c.transformacao ?? null })),
    camposOrdenar: (cfg.camposOrdenar ?? []).map(o => ({ campo: o.campo ?? '', direcao: o.direcao === 'DESC' ? 'DESC' : 'ASC' })),
    tratamentos: (cfg.tratamentos ?? []).map(t => ({
      ...novoTratamento(),
      ...t,
      nomeCampo: t.nomeCampo ?? '',
      nomeDestino: t.nomeDestino ?? '',
      separador: t.separador ?? '',
      formula: t.formula ?? '',
      filtros: (t.filtros ?? []).map(f => ({ campo: f.campo ?? '', tipoOperador: f.tipoOperador, valor: f.valor ?? '' })),
      camposAdicionais: (t.camposAdicionais ?? []).map(c => ({ ...c })),
      camposOrdenar: (t.camposOrdenar ?? []).map(o => ({ campo: o.campo ?? '', direcao: o.direcao === 'DESC' ? 'DESC' as const : 'ASC' as const })),
      variaveis: (t.variaveis ?? []).map(v => ({
        nome: v.nome ?? '', campo: v.campo ?? '', valorDefault: v.valorDefault ?? null,
        filtros: (v.filtros ?? []).map(f => ({ campo: f.campo ?? '', tipoOperador: f.tipoOperador, valor: f.valor ?? '' }))
      }))
    }))
  };
}

const limparFiltros = (filtros: AgrupamentoFiltro[]) => filtros.map(f => ({
  campo: f.campo.trim(),
  tipoOperador: f.tipoOperador,
  ...(operadorSemValor(f.tipoOperador) ? {} : { valor: (f.valor ?? '').trim() })
}));

/** Monta o objeto gravado em Recurso.configuracoes.agrupamento: cada tratamento leva só o que a sua operação usa */
export function montarAgrupamento(form: AgrupamentoConfigForm): AgrupamentoConfig {
  return {
    excluiRegistros: form.excluiRegistros,
    chaves: form.chaves.map(c => ({ campo: c.campo.trim(), transformacao: c.transformacao })),
    camposOrdenar: form.camposOrdenar.map(o => ({ campo: o.campo.trim(), direcao: o.direcao })),
    tratamentos: form.tratamentos.map(t => {
      const op = t.tipoOperacao;
      const saida: Partial<AgrupamentoTratamento> = { tipoOperacao: op };
      if (!semCampoFonte(op)) saida.nomeCampo = (t.nomeCampo ?? '').trim();
      const destino = (t.nomeDestino ?? '').trim();
      if (destino) saida.nomeDestino = destino;
      if (t.filtros.length > 0) saida.filtros = limparFiltros(t.filtros);
      if (aceitaCamposAdicionais(op) && t.camposAdicionais.length > 0) {
        saida.camposAdicionais = t.camposAdicionais.map(c => ({ nomeCampo: c.nomeCampo.trim(), nomeDestino: c.nomeDestino.trim() }));
      }
      if (aceitaOrdenacao(op) && t.camposOrdenar.length > 0) {
        saida.camposOrdenar = t.camposOrdenar.map(o => ({ campo: o.campo.trim(), direcao: o.direcao }));
      }
      if (op === 'CONCATENAR' && (t.separador ?? '') !== '') saida.separador = t.separador;
      if (op === 'FORMULA') {
        saida.formula = (t.formula ?? '').trim();
        saida.variaveis = t.variaveis.map(v => ({
          nome: v.nome.trim(),
          campo: v.campo.trim(),
          ...(v.valorDefault !== null && v.valorDefault !== undefined ? { valorDefault: v.valorDefault } : {}),
          filtros: limparFiltros(v.filtros)
        }));
      }
      return saida;
    })
  };
}

const PALAVRAS_SPEL = new Set(['and', 'or', 'not', 'true', 'false', 'null', 'eq', 'ne', 'lt', 'gt', 'le', 'ge', 'div', 'mod', 'matches', 'instanceof', 'between']);

/** Identificadores da fórmula que não são variáveis declaradas (ignora textos entre aspas, propriedades, métodos e #variáveis) */
export function variaveisNaoDeclaradas(formula: string, variaveis: { nome: string }[]): string[] {
  const semTextos = (formula ?? '').replace(/'[^']*'|"[^"]*"/g, ' ');
  const declaradas = new Set(variaveis.map(v => v.nome.trim()).filter(n => n));
  const achadas = new Set<string>();
  const re = /(?<![\w.#$])([A-Za-z_][A-Za-z0-9_]*)(?!\s*\()/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(semTextos)) !== null) {
    const nome = m[1];
    if (!declaradas.has(nome) && !PALAVRAS_SPEL.has(nome.toLowerCase())) achadas.add(nome);
  }
  return [...achadas];
}

function validarFiltros(filtros: AgrupamentoFiltro[], onde: string): string | null {
  for (const [i, f] of filtros.entries()) {
    if (!f.campo.trim()) return `${onde}, filtro ${i + 1}: informe o campo.`;
    if (!operadorSemValor(f.tipoOperador) && !(f.valor ?? '').trim()) return `${onde}, filtro ${i + 1}: informe o valor.`;
  }
  return null;
}

/** Primeiro problema do formulário, ou null se está pronto para salvar */
export function validarAgrupamento(form: AgrupamentoConfigForm): string | null {
  if (form.chaves.length === 0) return 'Informe ao menos um campo de agrupamento (chave).';
  for (const [i, c] of form.chaves.entries()) {
    if (!c.campo.trim()) return `Chave ${i + 1}: informe o campo.`;
  }
  for (const [i, o] of form.camposOrdenar.entries()) {
    if (!o.campo.trim()) return `Ordenação ${i + 1}: informe o campo.`;
  }
  if (form.tratamentos.length === 0) return 'Informe ao menos um tratamento (o que calcular por grupo).';
  const destinos = new Map<string, number>();
  for (const [i, t] of form.tratamentos.entries()) {
    const onde = `Tratamento ${i + 1}`;
    const campo = (t.nomeCampo ?? '').trim();
    const destino = (t.nomeDestino ?? '').trim() || (semCampoFonte(t.tipoOperacao) ? '' : campo);
    if (!semCampoFonte(t.tipoOperacao) && !campo) return `${onde}: informe o campo de origem.`;
    if (!destino) return `${onde}: informe o campo de destino.`;
    const chaveDestino = destino.toLowerCase();
    if (destinos.has(chaveDestino)) return `${onde}: o campo de destino "${destino}" já é usado no tratamento ${destinos.get(chaveDestino)}.`;
    destinos.set(chaveDestino, i + 1);
    const pFiltros = validarFiltros(t.filtros, onde);
    if (pFiltros) return pFiltros;
    if (aceitaCamposAdicionais(t.tipoOperacao)) {
      for (const [k, c] of t.camposAdicionais.entries()) {
        if (!c.nomeCampo.trim() || !c.nomeDestino.trim()) return `${onde}, campo adicional ${k + 1}: informe a origem e o destino.`;
      }
    }
    if (aceitaOrdenacao(t.tipoOperacao)) {
      for (const [k, o] of t.camposOrdenar.entries()) {
        if (!o.campo.trim()) return `${onde}, ordenação ${k + 1}: informe o campo.`;
      }
    }
    if (t.tipoOperacao === 'FORMULA') {
      if (!(t.formula ?? '').trim()) return `${onde}: informe a fórmula.`;
      const nomes = t.variaveis.map(v => v.nome.trim().toLowerCase());
      for (const [k, v] of t.variaveis.entries()) {
        if (!v.nome.trim() || !v.campo.trim()) return `${onde}, variável ${k + 1}: informe o nome e o campo.`;
        if (nomes.indexOf(v.nome.trim().toLowerCase()) !== k) return `${onde}: a variável "${v.nome.trim()}" está repetida.`;
        const pv = validarFiltros(v.filtros, `${onde}, variável ${v.nome.trim()}`);
        if (pv) return pv;
      }
      const faltando = variaveisNaoDeclaradas(t.formula!, t.variaveis);
      if (faltando.length > 0) return `${onde}: a fórmula usa ${faltando.length === 1 ? 'a variável não declarada' : 'as variáveis não declaradas'} ${faltando.join(', ')}.`;
    }
  }
  return null;
}
